import { RootStore, RootStoreProps } from '@intrasync/stores';
import { utils, camelToTitle } from '@intrasync/utils';
import localForage from 'localforage';
import { makeAutoObservable, reaction, runInAction } from 'mobx';

interface Service {
  path: string;
}

class DatabaseManagerStore {
  rootStore: RootStore;

  loading = false;
  isReady = false;
  availableServices: Service[] = [];

  sortState = {
    sortBy: [] as string[],
    sortDirection: {} as { [key: string]: string },
  };
  filterState: { [key: string]: string } = {};

  currentServiceItems: { [key: string]: { total: number; data: unknown[] } } =
    {};

  currentServer = 'production';
  currentApp:
    | (RootStore & {
      apiServicesStore?: { find: () => Promise<Service[]> };
    })
    | undefined = undefined;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;

    this.currentApp = rootStore;

    reaction(
      () => this.currentHeaders,
      (currentHeaders) => {
        if (currentHeaders) {
          // console.log('reaction to params', readableJS(currentHeaders));
          if (!this.rootStore.dataGridStore?.namespaces?.entityViewerBody)
            return;

          this.rootStore.dataGridStore.namespaces.entityViewerBody.setColumnHeaders(
            currentHeaders.map((headerKey: string) => {
              let type = 'string';
              if (
                headerKey.includes('Date') ||
                headerKey === 'removed' ||
                headerKey === 'rearViewIFC'
              ) {
                if (!headerKey.includes('EditedBy')) {
                  type = 'date';
                }
              }
              if (headerKey.includes('Time')) {
                type = 'time';
              }
              if (headerKey === 'id' || headerKey === 'jobNumber') {
                type = 'number';
              }
              if (
                headerKey.includes('Allowed') ||
                headerKey.includes('Archived') ||
                headerKey.includes('archived') ||
                headerKey.includes('Modeled') ||
                headerKey.includes('Drawn') ||
                headerKey.includes('Checked') ||
                headerKey === 'required' ||
                headerKey === 'adminUser' ||
                headerKey === 'isValidated'
              ) {
                type = 'boolean';
              }
              return {
                disableEdit:
                  headerKey === 'id' || this.selectedService === 'channel',
                key: headerKey,
                title: camelToTitle(headerKey),
                dataType: type,
                width: 200,
              };
            })
          );
        }
      },
      { delay: 200 }
    );
  }

  resetSortState = () => {
    this.sortState = { sortBy: [], sortDirection: {} };
  };

  resetFilterState = () => {
    this.filterState = {};
  };

  setServer = (server: string) => {
    const { serviceStores } = this.rootStore;
    if (server === this.currentServer) return;
    if (server === 'cloud-dev' || server === 'local-dev') {
      this.currentServer = server;
      this.currentServiceItems = {};
      this.availableServices = [];
      this.currentApp = undefined;
      // console.log('setting currentServer:', server);
      const currentRootStoreConfig: RootStoreProps = {
        appName:
          server === 'local-dev'
            ? `${this.rootStore.appName}-local-dev-entity-viewer`
            : `${this.rootStore.appName}-cloud-dev-entity-viewer`,
        storage: localForage,
        mode: 'development',
        appVersion: this.rootStore.appVersion,
        asyncStorage: true,
        autoUpdate: false,
        enableNotificationStore: false,
        enableAuthStore: { debugMode: false },
        serviceStores: serviceStores,
        disableViewStore: true,
        apiOptions:
          server === 'local-dev'
            ? {
              uriOverride: {
                development: 'http://localhost:3355',
              },
            }
            : {},
        appTitle: this.rootStore.appTitle,
        storageName: this.rootStore.storageName,
        viewStoreProps: this.rootStore.viewStoreProps,
      };
      this.currentApp = new RootStore(currentRootStoreConfig);
      // console.log('setting this.currentApp:', this.currentApp);
      return this.findServiceStores();
    }
    this.currentServer = 'production';
    this.currentServiceItems = {};
    this.availableServices = [];
    this.currentApp = undefined;
    this.currentApp = this.rootStore;
    return this.findServiceStores();
  };

  setupChannelListeners = (servicePath: string) => {
    console.log('DEBUGGGG setting up channel listeners for:', servicePath);
    if (!this.currentApp?.app.service(servicePath)) {
      throw new Error(`That service is not available: ${servicePath}`);
    }
    if (!this.selectedService) {
      throw new Error('No service selected');
    }
    this.currentApp.app.service(servicePath).on('created', (created) => {
      if (this.currentServiceItems[this.selectedService]?.data) {
        this.currentServiceItems[this.selectedService].data.push(created);
      }
    });
    this.currentApp.app.service(servicePath).on('patched', (patched) => {
      if (this.currentServiceItems[this.selectedService]?.data) {
        const foundIndex = this.currentServiceItems[
          this.selectedService
        ].data.findIndex((item) =>
          utils.isSameId((item as { id: string | number }).id, patched.id)
        );
        if (foundIndex > -1) {
          this.currentServiceItems[this.selectedService].data[foundIndex] =
            patched;
        }
      }
    });
    this.currentApp.app.service(servicePath).on('removed', (removed) => {
      if (this.currentServiceItems[this.selectedService]?.data) {
        const newArray = this.currentServiceItems[
          this.selectedService
        ].data.filter(
          (item) => !utils.isSameId((item as { id: string | number }).id, removed.id)
        );
        this.currentServiceItems[this.selectedService].data = newArray;
      }
    });
  };

  setSelectedService = (servicePath: string, needsRouting = true) => {
    console.log('DEBUGGGG setting selected service:', servicePath);
    console.log('DEBUGGGG needsRouting:', needsRouting);
    if (!this.currentApp?.app.service(servicePath)) {
      throw new Error(`That service is not available: ${servicePath}`);
    }
    if (needsRouting && this.rootStore.routerStore) {
      this.rootStore.routerStore.goTo(null, { servicePath: servicePath });
    }
    this.resetSortState();
    this.resetFilterState();

    // this.selectedService = servicePath;
    this.findServiceItems().then(() => {
      this.setupChannelListeners(servicePath);
      runInAction(() => {
        this.loading = false;
      });
    });
    // Need to add event handlers for created/patched/removed
    return servicePath;
  };

  findServiceStores = async () => {
    if (!this.currentApp?.apiServicesStore) return;
    const foundServices: Service[] =
      (await this.currentApp.apiServicesStore.find().catch((err) => {
        console.log('something went wrong finding services', err);
        return [];
      })) || [];
    console.log('foundServices:', foundServices);
    if (foundServices?.length > 0) {
      runInAction(() => {
        this.availableServices = foundServices
          ?.slice()
          .filter((i: Service) => {
            if (i.path.includes('autodesk')) return false;
            if (i.path.includes('azure')) return false;
            if (i.path.includes('sage')) return false;
            if (i.path.includes('auth')) return false;
            if (i.path.includes('api-services')) return false;
            return true;
          })
          .sort((a: Service, b: Service) => (a.path < b.path ? -1 : 1));
        if (!this.selectedService) {
          this.setSelectedService(this.availableServices[0].path);
        }
        if (
          this.selectedService &&
          !this.currentServiceItems[this.selectedService]
        ) {
          this.setSelectedService(this.selectedService, false);
        }
      });
    }
  };

  findServiceItems = async () => {
    console.log('DEBUGGGG finding service items');
    if (!this.currentApp?.app) return;
    const foundTotals = await this.currentApp.app
      .service(this.selectedService)
      .find({ query: { $limit: 0 } })
      .catch((err) => {
        console.log('something went wrong finding services', err);
      });
    console.log('DEBUGGGG foundTotals:', foundTotals);
    if (!foundTotals || foundTotals?.total === 0) {
      runInAction(() => {
        this.currentServiceItems[this.selectedService] = {
          total: 0,
          data: [],
        };
      });
      const emptyReturnNotificationMessage = `Found ${foundTotals.total} items for ${this.selectedService}`;
      return this.rootStore.notificationStore
        ? this.rootStore.notificationStore.addNotification({
          message: emptyReturnNotificationMessage,
          options: { variant: 'info' },
        })
        : console.log(emptyReturnNotificationMessage);
    }
    if (foundTotals?.total > 20000) {
      runInAction(() => {
        this.currentServiceItems[this.selectedService] = {
          total: foundTotals?.total,
          data: [],
        };
      });
      return this.rootStore.notificationStore
        ? this.rootStore.notificationStore.addNotification({
          message: `Found ${foundTotals.total} items for ${this.selectedService}, too many to try to fetch, 20k is current max`,
          options: { variant: 'warning' },
        })
        : console.log(
          `Found ${foundTotals.total} items for ${this.selectedService}, too many to try to fetch, 20k is current max`
        );
    }
    this.rootStore.notificationStore
      ? this.rootStore.notificationStore.addNotification({
        message: `Found ${foundTotals.total} items for ${this.selectedService}, fetching all`,
        options: { variant: 'info' },
      })
      : console.log(
        `Found ${foundTotals.total} items for ${this.selectedService}, fetching all`
      );
    const loopCount = Math.ceil(foundTotals?.total / 1000);
    const looper = Array.from(Array(loopCount).keys());

    runInAction(() => {
      this.currentServiceItems[this.selectedService] = {
        total: foundTotals?.total,
        data: [],
      };
    });
    Promise.all(
      looper.map(async (runNumber) => {
        const foundItems = this.currentApp?.app
          ? await this.currentApp.app
            .service(this.selectedService)
            .find({
              query: {
                $limit: 1000,
                $skip: 1000 * runNumber,
              },
            })
            .catch((err) => {
              console.log('something went wrong finding services', err);
            })
          : undefined;
        if (foundItems?.data?.length > 0) {
          return foundItems.data;
        }
      })
    ).then((returnedFromPromises) => {
      const flattened = returnedFromPromises.flat();
      runInAction(() => {
        this.currentServiceItems[this.selectedService].data = flattened;
        this.resetSortState();
        this.resetFilterState();
      });
    });
  };

  setFilter = (columnKey: string, filterValue: string) => {
    if (filterValue === '' && this.filterState?.[columnKey]) {
      delete this.filterState[columnKey];
      return;
    }
    this.filterState[columnKey] = filterValue;
  };

  handleMultiSort = ({
    sortBy,
    sortDirection,
  }: {
    sortBy: string;
    sortDirection: string;
  }) => {
    if (this.sortState.sortBy.includes(sortBy)) {
      runInAction(() => {
        this.sortState.sortDirection[sortBy] = sortDirection;
      });
      return this.sortState;
    }
    if (
      this.sortState?.sortBy?.length === 4 &&
      this.rootStore.notificationStore
    ) {
      return this.rootStore.notificationStore.addNotification({
        message: 'You can only sort up to 4 columns',
        options: { variant: 'info' },
      });
    }
    runInAction(() => {
      this.sortState.sortBy.push(sortBy);
      this.sortState.sortDirection[sortBy] = sortDirection;
    });
    return this.sortState;
  };

  removeSortBy = (sortBy: string) => {
    runInAction(() => {
      this.sortState.sortBy = this.sortState.sortBy.filter(
        (sorted) => !sorted.includes(sortBy)
      );
      delete this.sortState.sortDirection[sortBy];
    });
  };

  get selectedService() {
    const { routerStore } = this.rootStore;
    const servicePath = routerStore?.params?.servicePath as string;
    return servicePath || '';
  }

  get availableServiceNames() {
    return this.availableServices?.slice().map((i) => i.path);
  }

  get entities() {
    // console.log('DEBUGGGG getting entities', this.currentServiceItems);
    return this.currentServiceItems[this.selectedService]?.data?.slice() || [];
  }

  get currentHeaders() {
    if (
      !this.currentServiceItems[this.selectedService]?.data ||
      !this.currentServiceItems[this.selectedService]?.data[0]
    )
      return ['id'];
    const firstEntityKeys = Object.keys(
      this.currentServiceItems[this.selectedService]?.data[0] as object
    );
    if (firstEntityKeys.includes('id')) {
      const currentKeys = firstEntityKeys
        .slice()
        .filter((i) => i !== 'id')
        .sort((a, b) => (a < b ? -1 : 1));
      return ['id', ...currentKeys];
    }
    if (firstEntityKeys.includes('connectionId')) {
      const currentKeys = firstEntityKeys
        .slice()
        .filter((i) => i !== 'connectionId')
        .sort((a, b) => (a < b ? -1 : 1));
      return ['connectionId', ...currentKeys];
    }
    return [...firstEntityKeys];
  }

  get dataGridStoreCurrentData() {
    if (
      !this.rootStore?.dataGridStore?.namespaces?.entityViewerBody?.currentData
    ) {
      return [];
    }
    const currentData =
      this.rootStore?.dataGridStore?.namespaces?.entityViewerBody?.currentData;
    return currentData;
  }

  get dataGridStoreCurrentDataLength() {
    // return this.entities.length
    return this.dataGridStoreCurrentData.length;
  }
}

export default DatabaseManagerStore;
